import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "../styles/AdminUsers.css";

import {
  USERS_API,
  DELETE_USER_API,
  EDIT_USER_API,
} from "../config/api";
import { showConfirm, showAlert } from "../utils/customDialog";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ================= EDIT USER MODAL STATES =================
  const [editingUser, setEditingUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    pincode: "",
    address: "",
  });

  const handleCloseModal = () => {
    setEditingUser(null);
  };

  // ================= FILTERS =================
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");

  // ================= PAGINATION =================
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  // ================= FETCH USERS =================
  const fetchUsers = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(USERS_API);

      if (!res.ok) {
        if (res.status === 504 && retryCount < 1) {
          console.warn("[Users] 504 Gateway Timeout, retrying in 2 seconds...");
          setTimeout(() => fetchUsers(retryCount + 1), 2000);
          return;
        }
        if (res.status === 504) {
          throw new Error("Server Gateway Timeout (HTTP 504). Please click 'Retry' below.");
        }
        throw new Error(`Server returned HTTP ${res.status}: ${res.statusText}`);
      }

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        throw new Error("Invalid response format received from server. Please click 'Retry'.");
      }

      if (data.status === "success") {
        setUsers(data.users || []);
      } else {
        setError(data.message || "Failed to load users");
      }
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);



  // ================= DELETE USER =================
  const deleteUser = async (email) => {
    const ok = await showConfirm({
      title: "Delete User",
      message: `Are you sure you want to delete user ${email}?`,
      subMessage: "This will permanently remove the user account and associated profile data.",
      confirmText: "Delete User",
      type: "danger"
    });
    if (!ok) return;

    try {
      const res = await fetch(DELETE_USER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (data.status === "success") {
        fetchUsers();
      } else {
        alert(data.message || "Delete failed");
      }
    } catch (err) {
      alert(err.message || "Server error");
    }
  };

  // ================= EDIT USER =================
  const handleEditClick = (user) => {
    setEditingUser(user);

    setEditForm({
      id: user.id || "",
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      city: user.city || "",
      pincode: user.pincode || "",
      address: user.address || "",
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const res = await fetch(EDIT_USER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (data.status === "success") {
        setEditingUser(null);
        fetchUsers();
        showAlert({
          title: "Profile Updated",
          message: "User profile details have been successfully updated.",
          type: "success"
        });
      } else {
        showAlert({
          title: "Update Failed",
          message: data.message || "Failed to update user profile.",
          type: "danger"
        });
      }
    } catch (err) {
      showAlert({
        title: "Server Error",
        message: err.message || "An unexpected error occurred while updating the user.",
        type: "danger"
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ================= FILTER LOGIC =================
  const filteredUsers = users.filter((user) => {
    const searchText = search.toLowerCase();

    const matchSearch =
      user.name?.toLowerCase().includes(searchText) ||
      user.email?.toLowerCase().includes(searchText) ||
      user.phone?.toLowerCase().includes(searchText);

    const matchCity =
      cityFilter === "all" ||
      user.city?.toLowerCase() === cityFilter.toLowerCase();

    return matchSearch && matchCity;
  });

  // ================= PAGINATION =================
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  const currentUsers = filteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  // ================= RESET PAGE ON FILTER CHANGE =================
  useEffect(() => {
    setCurrentPage(1);
  }, [search, cityFilter, usersPerPage]);

  /* ================= UI ================= */
  return (
    <div className="admin-container users-admin-container admin-portal-root">
      {/* ================= HEADER ================= */}
      <div className="users-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-people-fill"></i> User Accounts Management
          </h1>
          <p>Monitor, search, and manage registered customer credentials and details</p>
        </div>

        {/* Dashboard Quick Stats */}
        <div className="users-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon users-total"><i className="bi bi-people-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{users.length}</span>
              <span className="metric-label">Total Users</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon cities-total"><i className="bi bi-geo-alt-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">
                {[...new Set(users.map((u) => u.city).filter(Boolean))].length}
              </span>
              <span className="metric-label">Cities Covered</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon orders-total"><i className="bi bi-bag-check-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">
                {users.reduce((sum, u) => sum + Number(u.order_count || 0), 0)}
              </span>
              <span className="metric-label">Orders Placed</span>
            </div>
          </div>
        </div>
      </div>

      {/* ================= FILTERS DECK ================= */}
      <div className="users-filter-deck">
        <div className="search-box-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="admin-search-input"
            style={{ paddingLeft: "48px" }}
            placeholder="Search by name, email, or phone number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-select-wrapper">
          <i className="bi bi-funnel-fill"></i>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="all">All Cities</option>
            {[...new Set(users.map((u) => u.city).filter(Boolean))].map((city, i) => (
              <option key={i} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ================= TABLE / CONTENT ================= */}
      {loading ? (
        <div className="users-loading-box">
          <div className="spinner"></div>
          <p>Loading registered user accounts...</p>
        </div>
      ) : error ? (
        <div className="users-error-box" style={{ maxWidth: "560px", margin: "40px auto", padding: "30px", background: "#ffffff", borderRadius: "14px", border: "1px solid #ebdcb9", textAlign: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "36px", color: "#d97706", display: "inline-block", marginBottom: "12px" }}></i>
          <h3 style={{ margin: "0 0 8px 0", color: "#4a1216", fontWeight: "750" }}>Unable to Load Users</h3>
          <p style={{ color: "#64748b", fontSize: "14px", lineHeight: "1.5", margin: "0 0 20px 0" }}>{error}</p>
          <button
            type="button"
            onClick={() => fetchUsers()}
            style={{
              padding: "9px 20px",
              background: "linear-gradient(135deg, #4a1216 0%, #6b1d24 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontWeight: "700",
              fontSize: "13.5px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            <i className="bi bi-arrow-clockwise"></i> Retry Loading Users
          </button>
        </div>
      ) : (
        <section className="users-table-section">
          {filteredUsers.length === 0 ? (
            <div className="users-empty-state">
              <i className="bi bi-person-x-fill"></i>
              <h3>No Users Found</h3>
              <p>We couldn't find any users matching your query parameters.</p>
            </div>
          ) : (
            <>
              <div className="users-table-wrapper">
                <table className="users-admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>Avatar</th>
                      <th>Email Address</th>
                      <th>Full Name</th>
                      <th>Phone Number</th>
                      <th>Location (City)</th>
                      <th>Pincode</th>
                      <th>Orders</th>
                      <th>Last Update</th>
                      <th style={{ width: "120px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.map((user, index) => (
                      <tr key={user.email || index}>
                        {/* AVATAR */}
                        <td>
                          {user.profile_image ? (
                            <img
                              src={`${USERS_API}${user.profile_image}`}
                              alt="avatar"
                              className="user-profile-avatar"
                            />
                          ) : (
                            <div className="user-avatar-placeholder">
                              {user.name ? user.name.charAt(0).toUpperCase() : "?"}
                            </div>
                          )}
                        </td>

                        {/* EMAIL */}
                        <td className="user-email-td">
                          <strong>{user.email}</strong>
                        </td>

                        {/* NAME */}
                        <td className="user-name-td">{user.name}</td>

                        {/* PHONE */}
                        <td>{user.phone}</td>

                        {/* CITY */}
                        <td>
                          <span className="user-city-pill">
                            {user.city || "N/A"}
                          </span>
                        </td>

                        {/* PINCODE */}
                        <td>{user.pincode || "N/A"}</td>

                        {/* ORDER COUNT */}
                        <td className="user-orders-td">
                          {user.order_count || 0}
                        </td>

                        {/* UPDATED */}
                        <td className="user-updated-td">
                          {user.updated_at
                            ? new Date(user.updated_at).toLocaleDateString("en-IN", { dateStyle: "medium" })
                            : "N/A"}
                        </td>

                        {/* ACTIONS */}
                        <td>
                          <div className="table-actions-row">
                            <button
                              type="button"
                              className="action-btn-edit"
                              onClick={() => handleEditClick(user)}
                              title="Edit User Profile"
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button
                              type="button"
                              className="action-btn-delete"
                              onClick={() => deleteUser(user.email)}
                              title="Delete User"
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ================= PAGINATION ================= */}
              {totalPages > 1 && (
                <div className="users-pagination">
                  <div className="pagination-info">
                    <span>
                      Showing <strong>{indexOfFirstUser + 1}</strong>–<strong>{Math.min(indexOfLastUser, filteredUsers.length)}</strong> of <strong>{filteredUsers.length.toLocaleString()}</strong> users
                    </span>
                    <span className="page-count-badge">
                      Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                    </span>
                    <div className="per-page-selector">
                      <span>Show:</span>
                      <select
                        value={usersPerPage}
                        onChange={(e) => setUsersPerPage(Number(e.target.value))}
                        className="per-page-select"
                      >
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>

                  <div className="pagination-controls">
                    <button
                      type="button"
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="page-nav-btn page-edge-btn"
                      title="First Page"
                    >
                      <i className="bi bi-chevron-double-left"></i> First
                    </button>

                    <button
                      type="button"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="page-nav-btn"
                      title="Previous Page"
                    >
                      <i className="bi bi-chevron-left"></i> Prev
                    </button>

                    <div className="page-numbers">
                      {getPaginationRange().map((page, idx) =>
                        page === "..." ? (
                          <span key={`dots-${idx}`} className="page-ellipsis">
                            &hellip;
                          </span>
                        ) : (
                          <button
                            key={page}
                            type="button"
                            onClick={() => goToPage(page)}
                            className={`page-num-btn ${currentPage === page ? "active" : ""}`}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="page-nav-btn"
                      title="Next Page"
                    >
                      Next <i className="bi bi-chevron-right"></i>
                    </button>

                    <button
                      type="button"
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="page-nav-btn page-edge-btn"
                      title="Last Page"
                    >
                      Last <i className="bi bi-chevron-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ================= EDIT USER MODAL ================= */}
      {editingUser && createPortal(
        <div className="admin-portal-root users-modal-portal-wrapper">
          <div className="users-modal-overlay" onClick={handleCloseModal}>
            <div className="users-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <i className="bi bi-person-gear"></i> Edit User Profile
                </h3>
                <button
                  type="button"
                  className="close-modal-btn"
                  onClick={handleCloseModal}
                  title="Close"
                  style={{
                    width: "32px",
                    minWidth: "32px",
                    maxWidth: "32px",
                    height: "32px",
                    minHeight: "32px",
                    maxHeight: "32px",
                    padding: 0,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                  }}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: "14px", lineHeight: 1 }}></i>
                </button>
              </div>
              
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  
                  <div className="modal-grid-row">
                    <div className="modal-input-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="modal-input-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>

                  <div className="modal-grid-row">
                    <div className="modal-input-group">
                      <label>Phone Number</label>
                      <input
                        type="text"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        placeholder="10-digit mobile number"
                      />
                    </div>
                    <div className="modal-input-group">
                      <label>Location (City)</label>
                      <input
                        type="text"
                        value={editForm.city}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        placeholder="City / District"
                      />
                    </div>
                  </div>

                  <div className="modal-grid-row">
                    <div className="modal-input-group">
                      <label>Postal Pincode</label>
                      <input
                        type="text"
                        value={editForm.pincode}
                        onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                        placeholder="6-digit PIN code"
                      />
                    </div>
                  </div>

                  <div className="modal-input-group" style={{ marginBottom: 0 }}>
                    <label>Detailed Address</label>
                    <textarea
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="modal-actions-panel">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCloseModal}
                    disabled={editLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="save-btn"
                    disabled={editLoading}
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    {editLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" style={{ width: "13px", height: "13px", borderWidth: "2px" }} role="status"></span>
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2"></i> Save Profile Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminUsers;
